import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Input, Select, Modal, Form, message, Popconfirm, Descriptions } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, EyeOutlined } from '@ant-design/icons';
import './AdminCommon.css';

const { Option } = Select;
const { TextArea } = Input;

const ExpertManagement = () => {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editingExpert, setEditingExpert] = useState(null);
  const [viewingExpert, setViewingExpert] = useState(null);
  const [form] = Form.useForm();

  // 获取专家列表
  const fetchExperts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/admin/experts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const formattedExperts = (data.data || []).map(expert => ({
          id: expert.id,
          name: expert.name,
          type: expert.type,
          level: expert.level,
          status: expert.status,
          avatar: expert.avatar,
          bio: expert.bio,
          capabilities: expert.capabilities || [],
          consultationCount: expert.consultationCount || 0,
          rating: expert.rating || 0,
          price: expert.price || 0,
          unit: expert.unit || '元/次',
          rawData: expert,
        }));
        setExperts(formattedExperts);
      } else {
        message.error(data.message || '获取专家列表失败');
      }
    } catch (error) {
      console.error('获取专家列表失败:', error);
      message.error('获取专家列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExperts();
  }, []);

  // 筛选
  const filteredExperts = experts.filter(expert => {
    const matchSearch = !searchText ||
      (expert.name && expert.name.toLowerCase().includes(searchText.toLowerCase())) ||
      (expert.bio && expert.bio.toLowerCase().includes(searchText.toLowerCase()));
    const matchLevel = levelFilter === 'all' || String(expert.level) === levelFilter;
    return matchSearch && matchLevel;
  });

  const typeMap = {
    distribution: '发行专家',
    data: '数据专家',
    marketing: '营销专家',
    creation_audio: '音频创作专家',
    creation_video: '视频创作专家',
    creation_image: '图片创作专家',
    planning: '策划专家',
  };

  const levelMap = {
    1: { text: '官方', color: 'gold' },
    2: { text: '认证', color: 'blue' },
    3: { text: '社区', color: 'default' },
  };

  const statusMap = {
    0: { text: '离线', color: 'default' },
    1: { text: '在线', color: 'green' },
    2: { text: '忙碌', color: 'orange' },
  };

  // 查看
  const handleView = (record) => {
    setViewingExpert(record);
    setViewModalVisible(true);
  };

  // 新增
  const handleAdd = () => {
    setEditingExpert(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 编辑
  const handleEdit = (record) => {
    setEditingExpert(record);
    form.setFieldsValue({
      name: record.name,
      type: record.type,
      level: record.level,
      bio: record.bio || '',
      price: record.price,
      unit: record.unit,
      status: record.status ?? 1,
      capabilities: Array.isArray(record.capabilities) ? record.capabilities.join(',') : '',
    });
    setModalVisible(true);
  };

  // 删除
  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/admin/experts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        message.success('删除成功');
        fetchExperts();
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除专家失败:', error);
      message.error('删除失败');
    }
  };

  // 认证/取消认证
  const handleCertify = async (record) => {
    try {
      const token = localStorage.getItem('token');
      const newLevel = record.level === 1 ? 2 : 1; // 切换官方/认证
      const res = await fetch(`/api/admin/experts/${record.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ level: newLevel }),
      });
      const data = await res.json();
      if (data.success) {
        message.success(record.level === 1 ? '已取消官方认证' : '已设为官方认证');
        fetchExperts();
      } else {
        message.error(data.message || '操作失败');
      }
    } catch (error) {
      console.error('认证操作失败:', error);
      message.error('操作失败');
    }
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...values,
        capabilities: values.capabilities ? values.capabilities.split(',').map(t => t.trim()).filter(Boolean) : [],
      };

      if (editingExpert) {
        const res = await fetch(`/api/admin/experts/${editingExpert.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) {
          message.success('更新成功');
          setModalVisible(false);
          fetchExperts();
        } else {
          message.error(data.message || '更新失败');
        }
      } else {
        const res = await fetch('/api/admin/experts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) {
          message.success('创建成功');
          setModalVisible(false);
          fetchExperts();
        } else {
          message.error(data.message || '创建失败');
        }
      }
    } catch (error) {
      console.error('保存专家失败:', error);
      message.error('操作失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '专家名称', dataIndex: 'name', key: 'name' },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => typeMap[type] || type,
    },
    {
      title: '等级',
      dataIndex: 'level',
      key: 'level',
      render: (level) => (
        <Tag color={levelMap[level]?.color || 'default'}>
          {levelMap[level]?.text || level}
        </Tag>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={statusMap[status]?.color || 'default'}>
          {statusMap[status]?.text || status}
        </Tag>
      )
    },
    { title: '咨询次数', dataIndex: 'consultationCount', key: 'consultationCount' },
    { title: '评分', dataIndex: 'rating', key: 'rating' },
    { title: '价格', dataIndex: 'price', key: 'price', render: (p, r) => `${p} ${r.unit}` },
    {
      title: '操作',
      key: 'action',
      width: 260,
      render: (_, record) => (
        <Space>
          <Button icon={<EyeOutlined />} size="small" onClick={() => handleView(record)}>
            查看
          </Button>
          <Button
            icon={<CheckCircleOutlined />}
            size="small"
            type={record.level === 1 ? 'primary' : 'default'}
            onClick={() => handleCertify(record)}
          >
            {record.level === 1 ? '取消认证' : '认证'}
          </Button>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除该专家?"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button icon={<DeleteOutlined />} size="small" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1 className="page-title">专家管理</h1>
        <p className="page-subtitle">管理平台认证专家</p>
      </div>

      <Card>
        <div className="table-toolbar" style={{ marginBottom: 16 }}>
          <Space>
            <Input.Search
              placeholder="搜索专家"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
            <Select
              value={levelFilter}
              onChange={setLevelFilter}
              style={{ width: 120 }}
            >
              <Option value="all">全部等级</Option>
              <Option value="1">官方</Option>
              <Option value="2">认证</Option>
              <Option value="3">社区</Option>
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              添加专家
            </Button>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={filteredExperts}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingExpert ? '编辑专家' : '新增专家'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="专家名称"
            name="name"
            rules={[{ required: true, message: '请输入专家名称' }]}
          >
            <Input placeholder="张三" />
          </Form.Item>
          <Form.Item
            label="类型"
            name="type"
            rules={[{ required: true, message: '请选择专家类型' }]}
          >
            <Select placeholder="选择类型">
              {Object.entries(typeMap).map(([code, name]) => (
                <Option key={code} value={code}>{name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="等级"
            name="level"
            initialValue={2}
            rules={[{ required: true, message: '请选择等级' }]}
          >
            <Select placeholder="选择等级">
              <Option value={1}>官方</Option>
              <Option value={2}>认证</Option>
              <Option value={3}>社区</Option>
            </Select>
          </Form.Item>
          <Form.Item label="简介" name="bio">
            <TextArea rows={3} placeholder="专家简介" />
          </Form.Item>
          <Form.Item label="价格" name="price" initialValue={0}>
            <Input type="number" placeholder="200" />
          </Form.Item>
          <Form.Item label="价格单位" name="unit" initialValue="元/次">
            <Input placeholder="元/次" />
          </Form.Item>
          <Form.Item label="状态" name="status" initialValue={1}>
            <Select>
              <Option value={1}>在线</Option>
              <Option value={0}>离线</Option>
              <Option value={2}>忙碌</Option>
            </Select>
          </Form.Item>
          <Form.Item label="核心能力" name="capabilities" extra="多个能力用英文逗号分隔">
            <Input placeholder="平台规则解读, 元数据优化" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看详情弹窗 */}
      <Modal
        title="专家详情"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {viewingExpert && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="ID">{viewingExpert.id}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={statusMap[viewingExpert.status]?.color || 'default'}>
                {statusMap[viewingExpert.status]?.text || viewingExpert.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="专家名称">{viewingExpert.name}</Descriptions.Item>
            <Descriptions.Item label="类型">{typeMap[viewingExpert.type] || viewingExpert.type}</Descriptions.Item>
            <Descriptions.Item label="等级">
              <Tag color={levelMap[viewingExpert.level]?.color || 'default'}>
                {levelMap[viewingExpert.level]?.text || viewingExpert.level}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="价格">{viewingExpert.price} {viewingExpert.unit}</Descriptions.Item>
            <Descriptions.Item label="咨询次数">{viewingExpert.consultationCount}</Descriptions.Item>
            <Descriptions.Item label="评分">{viewingExpert.rating}</Descriptions.Item>
            <Descriptions.Item label="核心能力" span={2}>
              {(viewingExpert.capabilities || []).map(cap => (
                <Tag key={cap}>{cap}</Tag>
              ))}
            </Descriptions.Item>
            <Descriptions.Item label="简介" span={2}>
              {viewingExpert.bio || '暂无简介'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default ExpertManagement;
